import React, { useState, useEffect } from 'react';
import { connect, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import { setStationDataUrl } from '../../redux/station-data/station-data.actions';

import { setReloadStaticData } from '../../redux/user/user.actions';

import { DateTime, Settings } from "luxon";

import { ARROW_RIGHT } from '../../SVGs/svg.imports.js';

/**
 * Configure the Luxon time zone so that all
 * instances of DateTime use the "correct" local time
 *
 * @source https://moment.github.io/luxon/#/zones?id=changing-the-default-zone
 */
Settings.defaultZone = "Australia/Sydney";

const SearchFilters = ({ stationEndpointUrl, setStationDataUrl, staticDataUrl, staticHour, staticDate, setReloadStaticData }) => {

  // The closest hour to make an API call
  const maximumCallTime = DateTime.now().minus( { hours: 2 } ).hour;

  const currentDateISO = DateTime.now().toISODate();

  const maximumHourOption = maximumCallTime > staticHour ? maximumCallTime : staticHour

  const reloadStaticData = useSelector(state => state.user.reloadStaticData)

  const [ dateValue, setDateValue ] = useState(staticDate || DateTime.now().minus( { hours: 1 } ).toISODate() );
  const [ timeValue, setTimeValue ] = useState(maximumHourOption);
  const [ useStatic, setUseStatic ] = useState(staticDate === dateValue && Number(timeValue) === Number(staticHour));
  const [ dateErrorMessage, setDateErrorMessage ] = useState("");
  const [ timeErrorMessage, setTimeErrorMessage ] = useState("");
  const [ daylightSavings, setDaylightSavings ] = useState(false);

  /**
    * On component load set the data source to the Static File
    * unless the Static File has got stuck in a cache, then revert to API
    * also check for daylight savings to update text
    */
  useEffect( () => {
    if (maximumCallTime > staticHour) {
      const earliestAvailableDatetime = DateTime.now().minus( { hours: 2 } );
      const earliestAvailableIsoDate = earliestAvailableDatetime.toFormat('yyyy-LL-dd');
      const earliestAvailableHour = earliestAvailableDatetime.hour;
      setStationDataUrl( generateDataUrl(earliestAvailableIsoDate, earliestAvailableHour) );
    } else {
      setDaylightSavings(DateTime.now().isInDST)
      setStationDataUrl( staticDataUrl );
    }
  }, []);

  // If user selects date and time matching the Static File
  useEffect(() => {
    setUseStatic(staticDate === dateValue && Number(timeValue) === Number(staticHour));
  }, [dateValue, timeValue, staticDate, staticHour]);

  // If API is not responsive, reload Static File
  useEffect( () => {
    if (reloadStaticData) {
        setTimeValue(staticHour)
        setDateValue(staticDate)
        setStationDataUrl(staticDataUrl);
        setReloadStaticData(false)
    }
  }, [reloadStaticData]);

  /**
   * Given the start date and hour of the day,
   * calculate the start and end dates and times
   * needed for the API request for the station data.
   * Then assemble those into a URL.
   *
   * @param {String} startDateIso ISO 8601 format of the date to retrieve. e.g. 2021-09-12
   * @param {Integer} startHour The hour of the day to retrieve data for. i.e. 0-23
   *
   * @returns String A URL that can be used to GET station data
   */
  function generateDataUrl(startDateIso, startHour) {
    let isDaylightSavings = false;
    let startDatetime = null;
    let endDatetime = null;
    let endtime = null;

    /**
     * Determine the timezone of the provided
     * start date and time.
     */
    isDaylightSavings = DateTime.fromISO(`${startDateIso}T${hourTo24Format(startHour)}:00:00`).isInDST;

    // Update the local state
    setDaylightSavings(isDaylightSavings);

     if (isDaylightSavings) {
      // Subtract one additional hour from the API data request
      startDatetime = DateTime.fromISO(`${startDateIso}T${hourTo24Format(startHour)}:00:00`).minus( { hours: 1 } );
    } else {
      // Use the provided hour
      startDatetime = DateTime.fromISO(`${startDateIso}T${hourTo24Format(startHour)}:00:00`);
    }

    endDatetime = startDatetime.plus( { hours: 1 } );
    endtime = endDatetime.hour;

    /**
     * Catch the issue where adding 1 hour to the
     * start datetime using Luxon switches over
     * to AEDT and puts the range at 2 hours
     * instead of 1.
     */
    if (endtime !== 0 && endtime !== startDatetime.hour + 1) {
      endtime = startDatetime.hour + 1;
    }

    /**
     * Note: There is still an issue when the
     * user selects 3am - 4am on the day that
     * AEDT begins. The issue occurs because
     * 2am happens twice. Once for AEST timezone
     * and once again for AEDT when the clock
     * is set back. This means that for the
     * range 3am - 4am on the day AEDT begins,
     * we return data an hour earlier than intended.
     */

    // Note: stationEndpointUrl is a global constant passed into this component
    if (useStatic) {
     return staticDataUrl;
    } else {
      return `${stationEndpointUrl}?ts=${hourTo24Format(startDatetime.hour)}&te=${hourTo24Format(endtime)}&ds=${startDatetime.toFormat('yyyy-LL-dd')}&de=${endDatetime.toFormat('yyyy-LL-dd')}`;
    }
  }

  /**
   * Add leading zeros to an hour to convert a
   * single hour (0 - 23) into 24-hour (00 - 23) format
   *
   * @param {Integer} hour The hour to convert
   *
   * @returns String The hour expressed as two digits
   */
  function hourTo24Format(hour) {
    let formattedHour = hour;

    if (formattedHour < 10) {
      formattedHour = "0".concat(formattedHour);
    } else {
      formattedHour = "".concat(formattedHour);
    }

    return formattedHour;
  }

  /**
   * Capture the changes to the filter option inputs
   *
   * @param {Change Event} evt Event that triggered the change to the input
   *
   * @returns void
   */
  const onInputChange = (evt) => {
    const inputId = evt.currentTarget.getAttribute('id');

    evt.preventDefault();

    if (inputId === 'date-field') {
      setDateValue(evt.currentTarget.value);
      setDateErrorMessage('');
    }

    if (inputId === 'time-field') {
      setTimeValue(evt.currentTarget.value);
      setTimeErrorMessage('');
    }
  };

  /**
   * Submit the filters and trigger the request to get updated station data
   *
   * @param {Click Event} evt Event that triggered the click
   *
   * @returns void
   */
  const onFilterSubmit = (evt) => {
    let selectedTimeStart = parseInt(timeValue, 10);
    let selectedDateStart = dateValue;
    let currentDateComparison = DateTime.fromISO(currentDateISO);
    let pastDateComparison = currentDateComparison.minus({ months: 6 });
    let selectedDateComparison = DateTime.fromISO(selectedDateStart);

    evt.preventDefault();

    // Validation
    if (selectedDateComparison.startOf('day') > currentDateComparison.startOf('day')) {
      // Must be a current or past date
      setDateErrorMessage(`You must select today's date or a date in the past.`);
      return;
    }

    if (selectedDateComparison.startOf('day') < pastDateComparison.startOf('day')) {
      // Must be a date from the last 6 months
      setDateErrorMessage(`You must select a date from within the last 6 months.`);
      return;
    }

    if (isNaN(selectedTimeStart)) {
      // There must be a time value
      setTimeErrorMessage('You must select a time.');
      return;
    }

    if (selectedDateStart === currentDateISO && selectedTimeStart > maximumHourOption) {
      // If filtering by today's date, the time value must be in the past
      setTimeErrorMessage('You must select a time in the past.');
      return;
    }

    setStationDataUrl( generateDataUrl(selectedDateStart, selectedTimeStart) );
  }

  return (
    <div className="station-map-search-datetime-filters__container">

      <div className="sq-form-question sq-form-question-date">
        <label className="visuallyhidden" htmlFor="date-field">Date</label>
        <div className="sq-form-question-answer">
          <input onChange={onInputChange} type="date" className="sq-form-field" id="date-field" value={dateValue} max={ currentDateISO } min={ DateTime.now().minus({ months: 6 }).toISODate() } required={true} />
        </div>
        {
          (dateErrorMessage !== "") ?
          <p className="sq-form-error">{dateErrorMessage}</p>
          : null
        }
      </div>

      <div className={(timeErrorMessage !== "") ? 'sq-form-question sq-form-question-time sq-form-question-error' : 'sq-form-question sq-form-question-time'}>
        <label className="visuallyhidden" htmlFor="time-field">Time</label>
        <div className="sq-form-question-answer">
          <select onChange={onInputChange} className="sq-form-field" size="" value={timeValue} id="time-field">
            <option value="">Choose time</option>
            <option value="0">12 am - 1 am</option>
            <option value="1" disabled={ (1 > maximumHourOption && dateValue === currentDateISO) ? true : false } >1 am - 2 am</option>
            <option value="2" disabled={ (2 > maximumHourOption && dateValue === currentDateISO) ? true : false } >2 am - 3 am</option>
            <option value="3" disabled={ (3 > maximumHourOption && dateValue === currentDateISO) ? true : false } >3 am - 4 am</option>
            <option value="4" disabled={ (4 > maximumHourOption && dateValue === currentDateISO) ? true : false } >4 am - 5 am</option>
            <option value="5" disabled={ (5 > maximumHourOption && dateValue === currentDateISO) ? true : false } >5 am - 6 am</option>
            <option value="6" disabled={ (6 > maximumHourOption && dateValue === currentDateISO) ? true : false } >6 am - 7 am</option>
            <option value="7" disabled={ (7 > maximumHourOption && dateValue === currentDateISO) ? true : false } >7 am - 8 am</option>
            <option value="8" disabled={ (8 > maximumHourOption && dateValue === currentDateISO) ? true : false } >8 am - 9 am</option>
            <option value="9" disabled={ (9 > maximumHourOption && dateValue === currentDateISO) ? true : false } >9 am - 10 am</option>
            <option value="10" disabled={ (10 > maximumHourOption && dateValue === currentDateISO) ? true : false } >10 am - 11 am</option>
            <option value="11" disabled={ (11 > maximumHourOption && dateValue === currentDateISO) ? true : false } >11 am - 12 pm</option>
            <option value="12" disabled={ (12 > maximumHourOption && dateValue === currentDateISO) ? true : false } >12 pm - 1 pm</option>
            <option value="13" disabled={ (13 > maximumHourOption && dateValue === currentDateISO) ? true : false } >1 pm - 2 pm</option>
            <option value="14" disabled={ (14 > maximumHourOption && dateValue === currentDateISO) ? true : false } >2 pm - 3 pm</option>
            <option value="15" disabled={ (15 > maximumHourOption && dateValue === currentDateISO) ? true : false } >3 pm - 4 pm</option>
            <option value="16" disabled={ (16 > maximumHourOption && dateValue === currentDateISO) ? true : false } >4 pm - 5 pm</option>
            <option value="17" disabled={ (17 > maximumHourOption && dateValue === currentDateISO) ? true : false } >5 pm - 6 pm</option>
            <option value="18" disabled={ (18 > maximumHourOption && dateValue === currentDateISO) ? true : false } >6 pm - 7 pm</option>
            <option value="19" disabled={ (19 > maximumHourOption && dateValue === currentDateISO) ? true : false } >7 pm - 8 pm</option>
            <option value="20" disabled={ (20 > maximumHourOption && dateValue === currentDateISO) ? true : false } >8 pm - 9 pm</option>
            <option value="21" disabled={ (21 > maximumHourOption && dateValue === currentDateISO) ? true : false } >9 pm - 10 pm</option>
            <option value="22" disabled={ (22 > maximumHourOption && dateValue === currentDateISO) ? true : false } >10 pm - 11 pm</option>
            <option value="23" disabled={ (23 > maximumHourOption && dateValue === currentDateISO) ? true : false } >11 pm - 12 am</option>
          </select>
        </div>
        {
          (timeErrorMessage !== "") ?
          <p className="sq-form-error">{timeErrorMessage}</p>
          : null
        }
      </div>

      <p className="station-map-search-datetime-filters__timezone">
        { (daylightSavings) ? 'AEDT' : 'AEST' }
      </p>

      <button className="station-map-search-filters__button" onClick={onFilterSubmit}><span className="visuallyhidden">Apply station data filters</span><ARROW_RIGHT/></button>
    </div>
  );
};

SearchFilters.propTypes = {
  "stationEndpointUrl": PropTypes.string,
  "setStationDataUrl":  PropTypes.oneOfType( [PropTypes.string, PropTypes.func] ),
  "staticDataUrl": PropTypes.string,
  "staticHour": PropTypes.string,
  "staticDate": PropTypes.string,
  "setReloadStaticData": PropTypes.oneOfType( [PropTypes.bool, PropTypes.func] )
  };

const mapDispatchToProps = (dispatch) => {
  return {
    "setStationDataUrl": stationDataUrl => dispatch(setStationDataUrl(stationDataUrl)),
    "setReloadStaticData": boolean => dispatch(setReloadStaticData(boolean)),
  };
};

export default connect(null, mapDispatchToProps)(SearchFilters);
